import { memo, useCallback, useState } from 'react';

import {
	Box,
	Button,
	Flex,
	Group,
	LoadingOverlay,
	Pagination,
	ScrollArea,
	Table,
	Text,
} from '@mantine/core';

import PassForm from '@/components/pass-form/pass-form';
import PassesItem from '@/components/passes-item/passes-item';

import usePassesCheckout from '@/hooks/usePassesCheckout';

import { selectUser } from '@/store/user-process/user-selectors';

import { useGetPassTypesQuery } from '@/api/pass-types';
import {
	useCreatePassMutation,
	useDeletePassMutation,
	useLazyGetPersonPassesQuery,
	useUpdatePassMutation,
} from '@/api/passes';
import { useGetPersonsQuery } from '@/api/persons';

import { PassFormFields } from '@/types/passes';
import { PersonPass } from '@/types/person';

import { chunk } from '@/shared/helpers/utils';
import { useShowNotifaction } from '@/shared/hooks/useShowNotifaction.tsx';
import { Select } from '@/shared/uikit/components/input';
import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

import { useAppSelector } from '@/hooks';

function Passes() {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [pass, setPass] = useState<null | PersonPass>(null);
	const [person_id, setPersonId] = useState<number | null>(null);
	const [activePage, setPage] = useState(1);

	const user = useAppSelector(selectUser);

	const { data: persons, isLoading, isFetching } = useGetPersonsQuery({});

	const [
		getPersonPasses,
		{ data: personPasses, isLoading: isPersonPassesLoading, isFetching: isPersonPassesFetching },
	] = useLazyGetPersonPassesQuery();

	const {
		data: passTypes,
		isLoading: isPassTypesLoading,
		isFetching: isPassTypesFetching,
	} = useGetPassTypesQuery('passtypes');

	const [createPass, { isLoading: isCreateLoading }] = useCreatePassMutation();
	const [updatePass, { isLoading: isUpdateLoading }] = useUpdatePassMutation();
	const [deletePass] = useDeletePassMutation();

	const checkoutPasses = usePassesCheckout();

	const fetchPersonPasses = useCallback(
		async (value: number) => {
			setPersonId(value);
			await getPersonPasses(value).unwrap();
		},
		[getPersonPasses],
	);

	const handleEdit = (record: PersonPass) => {
		setPass(record);
		setIsModalOpen(true);
	};

	const handleClose = () => {
		setPass(null);
		setIsModalOpen(false);
	};

	const handleCreate = useCallback(
		async (body: PassFormFields) => {
			try {
				if (!person_id) {
					return;
				}
				await createPass({
					body: { ...body, person_id },
					id: person_id,
				}).unwrap();
				await checkoutPasses(true, user);
			} catch (e) {
				useShowNotifaction({ text: 'Не удалось удалить пропуск', type: 'error' });
			}
		},
		[checkoutPasses, createPass, person_id, user],
	);

	const handleUpdate = useCallback(
		async ({ id, body }: { id: number; body: PassFormFields }) => {
			try {
				if (!person_id) {
					return;
				}
				await updatePass({ id, body: { ...body, person_id } }).unwrap();
				await checkoutPasses(true, user);
			} catch (e) {
				useShowNotifaction({ text: 'Не удалось удалить пропуск', type: 'error' });
			}
		},
		[checkoutPasses, person_id, updatePass, user],
	);

	const handleDelete = useCallback(
		async (id: number) => {
			try {
				await deletePass(id).unwrap();
				await checkoutPasses(true, user);
			} catch (e) {
				useShowNotifaction({ text: (e as Error)?.message ?? '', type: 'error' });
			}
		},
		[checkoutPasses, deletePass, user],
	);

	const chunked = chunk(personPasses?.objects ?? [], 10);

	const rows = chunked[activePage - 1]?.map((pass) => (
		<PassesItem key={pass.id} pass={pass} handleEdit={handleEdit} handleDelete={handleDelete} />
	));

	return (
		<>
			<LayoutRow
				title={{
					title: { children: 'Пропуска', size: 'h4', fw: 800 },
				}}
				type="once"
				style={{ height: 'calc(100vh - 116px)' }}
			>
				<Box
					pos="relative"
					style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
				>
					<LoadingOverlay zIndex={1000} overlayProps={{ radius: 2, blur: 2 }} />
					<Group>
						<Select
							w={530}
							data={
								persons?.objects.map((elem) => {
									return { value: `${elem.id}`, label: elem.full_name };
								}) ?? []
							}
							limit={30}
							onChange={(value) => {
								fetchPersonPasses(Number(value));
							}}
							caption="ФИО сотрудника"
							placeholder="Поиск"
							searchable
						/>
						<Button
							variant="filled"
							size="lg"
							onClick={() => {
								if (person_id) setIsModalOpen(true);
							}}
							disabled={!person_id}
						>
							Добавить пропуск
						</Button>
					</Group>
					{personPasses?.objects ? (
						<>
							<ScrollArea h={'100vh'} pr={'md'} scrollbarSize={12} offsetScrollbars>
								<Table verticalSpacing="md" stickyHeader>
									<Table.Thead>
										<Table.Tr>
											<Table.Th>
												<Text size="md" c="dimmed">
													Тип
												</Text>
											</Table.Th>
											<Table.Th>
												<Text size="md" c="dimmed">
													Значение
												</Text>
											</Table.Th>
											<Table.Th>
												<Text size="md" c="dimmed">
													Создан
												</Text>
											</Table.Th>
											<Table.Th>
												<Text size="md" c="dimmed">
													Обновлён
												</Text>
											</Table.Th>
											<Table.Th>
												<Text size="md" c="dimmed">
													Действия
												</Text>
											</Table.Th>
										</Table.Tr>
									</Table.Thead>
									<Table.Tbody>{rows}</Table.Tbody>
								</Table>
							</ScrollArea>
							<Pagination total={chunked.length} value={activePage} onChange={setPage} mt="sm" />
						</>
					) : (
						<Flex direction="column" align="center" justify="center" h={'calc(100vh - 300px)'}>
							<Text fz={18} fw={700}>
								Пока ничего нет 😔
							</Text>
						</Flex>
					)}
				</Box>
			</LayoutRow>
			<PassForm
				isOpen={isModalOpen}
				pass={pass}
				passTypes={passTypes}
				onUpdate={handleUpdate}
				onCreate={handleCreate}
				handleClose={handleClose}
			/>
		</>
	);
}

export default memo(Passes);
